import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import './ApiDocs.css';

// サードパーティ開発者向け API（OAuth 2.0 + /api/*）のドキュメント。ログイン不要の公開ページ。
// 章立ては承認済みドラフト（1〜6章、VRoid Hub 風のリファレンス形式）に準拠する。各章 section には
// id="ch1"〜"ch6" を付け、固定ヘッダー分のオフセットを ApiDocs.css の scroll-margin-top で吸収している。
//
// このアプリは HashRouter（GitHub Pages 対応）を採用しているため、素の <a href="#ch1"> は
// ブラウザのハッシュ書き換え経由で React Router に "/ch1" というパスとして解釈され、
// 404 ページへ遷移してしまう。ページ内リンクは必ず ChapterLink 経由にし、
// クリック時は preventDefault + scrollIntoView で同一ページ内スクロールに留める。
function ChapterLink({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: ReactNode;
}) {
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <a href={`#${id}`} className={className} onClick={handleClick}>
      {children}
    </a>
  );
}

type Method = 'GET' | 'POST' | 'DELETE';

// エンドポイント表・見出しで使う HTTP メソッドチップ。
// GET = plan-pro（--color-plan-pro-* トークン）、POST = warning、DELETE = danger トークンで色分けする。
function MethodChip({ method }: { method: Method }) {
  const variant = method === 'GET' ? 'get' : method === 'POST' ? 'post' : 'del';
  return <span className={`apidocs-chip apidocs-chip--${variant}`}>{method}</span>;
}

function Yn({ yes }: { yes: boolean }) {
  return (
    <span className={`apidocs-yn ${yes ? 'apidocs-yn--yes' : 'apidocs-yn--no'}`}>
      {yes ? '○' : '✕'}
    </span>
  );
}

type FlowStep = {
  from: string;
  to: string;
  label: ReactNode;
  note?: ReactNode;
};

// 2-3節のフロー図で使う、縦タイムライン式シーケンス図。
// mermaid 等の外部ライブラリは使わず、番号バッジ + アクターチップ + 縦の接続線のみで組む。
function FlowDiagram({ steps }: { steps: FlowStep[] }) {
  return (
    <figure className="apidocs-figure">
      <ol className="apidocs-flow">
        {steps.map((step, i) => (
          <li className="apidocs-flow__step" key={i}>
            <div className="apidocs-flow__marker">
              <span className="apidocs-flow__num">{i + 1}</span>
              {i < steps.length - 1 && <span className="apidocs-flow__line" aria-hidden="true" />}
            </div>
            <div className="apidocs-flow__content">
              <div className="apidocs-flow__actors">
                <span className="apidocs-flow__actor">{step.from}</span>
                <span className="apidocs-flow__arrow" aria-hidden="true">
                  →
                </span>
                <span className="apidocs-flow__actor">{step.to}</span>
              </div>
              <div className="apidocs-flow__label">{step.label}</div>
              {step.note && <div className="apidocs-flow__note">{step.note}</div>}
            </div>
          </li>
        ))}
      </ol>
    </figure>
  );
}

export default function ApiDocs() {
  const base = import.meta.env.VITE_API_BASE_URL || 'https://api.example.com';
  // 認可 URL（/oauth/authorize）は API ドメインではなく、フロント（SPA）自身のルート。
  const siteBase = typeof window !== 'undefined' ? window.location.origin : 'https://vrmahub.com';

  // アクター表記（2-3節のフロー図で利用）。
  const actorUser = 'ユーザー';
  const actorClient = '連携アプリ（クライアント）';
  const actorServer = '連携アプリのバックエンド';
  const actorSite = `VRMA Hub（${siteBase}）`;
  const actorApi = 'VRMA Hub API';

  const toc: { n: number; id: string; title: string }[] = [
    { n: 1, id: 'ch1', title: 'はじめに — できること・認可方式' },
    { n: 2, id: 'ch2', title: '認証 — アプリ登録と OAuth 2.0' },
    { n: 3, id: 'ch3', title: '共通仕様 — ヘッダ・エンベロープ・型情報' },
    { n: 4, id: 'ch4', title: 'エンドポイントリファレンス' },
    { n: 5, id: 'ch5', title: 'ファイルのダウンロード' },
    { n: 6, id: 'ch6', title: '付録 — スコープ・エラーコード' },
  ];

  return (
    <div className="container">
      <div className="apidocs-eyebrow">VRMA Hub Developers</div>
      <div className="section-title">
        <h2>VRMA Hub 開発者 API ドキュメント</h2>
      </div>
      <p className="apidocs-lede">
        連携アプリから VRMA Hub のアニメーションを利用するための公式 API リファレンスです。
      </p>

      <nav className="apidocs-toc" aria-label="目次">
        <div className="apidocs-toc__title">目次</div>
        <ol className="apidocs-toc__list">
          {toc.map((item) => (
            <li key={item.id}>
              <ChapterLink id={item.id} className="apidocs-toc__link">
                <span className="apidocs-toc__num">{item.n}</span>
                <span>{item.title}</span>
              </ChapterLink>
            </li>
          ))}
        </ol>
      </nav>

      {/* ============================================================ */}
      <section className="apidocs-chapter" id="ch1">
        <div className="apidocs-chapter__head">
          <span className="apidocs-chapter__num">1</span>
          <h2>はじめに</h2>
        </div>

        <p>VRMA Hub 開発者 API を使うと、連携アプリから次のことができます。</p>
        <ul>
          <li>アクセストークンの持ち主（ユーザー本人）の情報取得</li>
          <li>ユーザー本人の投稿・お気に入りアニメーションの一覧取得</li>
          <li>公開アニメーションの新着一覧取得</li>
          <li>アニメーションの詳細取得・キーワード検索</li>
          <li>
            ダウンロードライセンス、または <code className="code-inline">GET /animations/{'{id}'}/file</code>
            により <code className="code-inline">.vrma</code> ファイル（サーバー側で復号済みの平文）を取得
          </li>
          <li>投稿者が連携アプリに「アプリ配信設定」したアニメーションの取得</li>
        </ul>
        <p>
          ベース URL: <code className="code-inline">{base}</code>
        </p>

        <p>
          アクセストークンの取得方法は2通りあります。詳細は<ChapterLink id="ch2">2章</ChapterLink>
          （2-3節・2-4節）を参照してください。
        </p>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th></th>
                <th>認可コードフロー（2-3節）</th>
                <th>client_credentials（2-4節）</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>ユーザーのログイン</strong>
                </td>
                <td>必要（同意画面あり）</td>
                <td>不要</td>
              </tr>
              <tr>
                <td>
                  <strong>トークンの名義</strong>
                </td>
                <td>ユーザー本人</td>
                <td>アプリ自身</td>
              </tr>
              <tr>
                <td>
                  <strong>refresh_token</strong>
                </td>
                <td>発行される</td>
                <td>発行されない（1時間ごとに再取得）</td>
              </tr>
              <tr>
                <td>
                  <strong>アクセスできる範囲</strong>
                </td>
                <td>同意された scope の範囲（本人の private 投稿を含む）</td>
                <td>
                  配信アセット + public のみ。<code className="code-inline">/api/account</code>・
                  <code className="code-inline">/api/hearts</code> は不可
                </td>
              </tr>
              <tr>
                <td>
                  <strong>利用できる API</strong>
                </td>
                <td>同意された scope の範囲ですべて</td>
                <td>配信アセット + public の読み取り・検索・ダウンロードのみ</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ============================================================ */}
      <section className="apidocs-chapter" id="ch2">
        <div className="apidocs-chapter__head">
          <span className="apidocs-chapter__num">2</span>
          <h2>認証</h2>
        </div>

        <h3 className="apidocs-h3">2-1. アプリの登録</h3>
        <p>
          <Link to="/developers/portal">開発者ポータル</Link>のマイページから、OAuth クライアントの{' '}
          <code className="code-inline">client_id</code> と <code className="code-inline">redirect_uri</code>
          （最大5件）を登録します。OAuth 連携の利用には client_secret の発行（2-2節）と PKCE（2-3節）が
          必須です。
        </p>

        <h3 className="apidocs-h3">2-2. client_secret</h3>
        <p>
          開発者ポータルのマイページから <code className="code-inline">client_secret</code>（
          <code className="code-inline">vhcs_...</code> 形式）を発行します。平文の client_secret はこの発行時の
          レスポンスと、発行直後にのみダウンロードできる Credential ファイルにしか含まれず、
          <strong>再表示・再ダウンロードはできません</strong>。
        </p>
        <p>
          client_secret が未発行の場合、<code className="code-inline">grant_type=authorization_code</code> /{' '}
          <code className="code-inline">refresh_token</code> と <code className="code-inline">/oauth/revoke</code>{' '}
          は <code className="code-inline">401 invalid_client</code>、
          <code className="code-inline">grant_type=client_credentials</code> は{' '}
          <code className="code-inline">400 unauthorized_client</code> になります。
        </p>
        <p>client_secret はどちらの方式でも渡せます。</p>
        <pre className="code-block">
          <code>{`# 方式1: ボディパラメータ
grant_type=authorization_code&code=...&client_id=...&code_verifier=...&redirect_uri=...&client_secret=vhcs_...

# 方式2: Authorization: Basic ヘッダ（base64(client_id:client_secret)）
Authorization: Basic \${base64(client_id + ":" + client_secret)}`}</code>
        </pre>
        <div className="apidocs-callout apidocs-callout--warn">
          <span className="apidocs-callout__label">重要 — client_secret の保管</span>
          <p style={{ margin: 0 }}>
            client_secret は自分が管理する<strong>サーバーサイドでのみ</strong>保管してください。ネイティブアプリ・
            SPA のように成果物がユーザー端末に配布されるクライアントには
            <strong>絶対に埋め込まないでください</strong>。漏洩した場合の対処は{' '}
            <ChapterLink id="ch2">2-4節</ChapterLink>を参照してください。
          </p>
        </div>

        <h3 className="apidocs-h3">2-3. 認可コードフロー + PKCE</h3>
        <p>
          ユーザー本人の名義でデータを読むための標準フローです。ユーザーが同意画面で許可した scope の範囲でのみ
          アクセスできます。
        </p>

        <FlowDiagram
          steps={[
            {
              from: actorClient,
              to: actorSite,
              label: (
                <>
                  <code className="code-inline">/oauth/authorize</code> へリダイレクト（client_id, scope, state,
                  code_challenge）
                </>
              ),
            },
            {
              from: actorUser,
              to: actorSite,
              label: <>ログインし、同意画面で許可</>,
            },
            {
              from: actorSite,
              to: actorClient,
              label: (
                <>
                  <code className="code-inline">redirect_uri?code=...&state=...</code>
                </>
              ),
            },
            {
              from: actorClient,
              to: actorServer,
              label: <>認可コードを渡す</>,
            },
            {
              from: actorServer,
              to: actorApi,
              label: (
                <>
                  <code className="code-inline">POST /oauth/token</code>（code + code_verifier + client_secret）
                </>
              ),
            },
            {
              from: actorApi,
              to: actorServer,
              label: <>access_token + refresh_token</>,
            },
            {
              from: actorServer,
              to: actorApi,
              label: (
                <>
                  <code className="code-inline">GET /api/account/animations</code>（Bearer +{' '}
                  <code className="code-inline">X-Api-Version: 1</code>）
                </>
              ),
            },
            {
              from: actorApi,
              to: actorServer,
              label: <>ユーザーの投稿一覧</>,
            },
          ]}
        />

        <p>
          <strong>認可 URL:</strong> ユーザーを次の URL に誘導します。認可 URL は API ドメインではなく、Web
          サイト側のページです。
        </p>
        <pre className="code-block">
          <code>{`${siteBase}/#/oauth/authorize
  ?client_id=...
  &redirect_uri=...
  &scope=read:animations
  &state=xyz
  &code_challenge=...
  &code_challenge_method=S256
  &response_type=code`}</code>
        </pre>

        <p>
          <strong>PKCE:</strong> <code className="code-inline">code_verifier</code> は 43〜128 文字の URL-safe な
          乱数文字列です。<code className="code-inline">code_challenge</code> はその SHA-256 ハッシュを base64url
          エンコードして作ります。<code className="code-inline">code_challenge_method=S256</code> 固定です。
        </p>
        <pre className="code-block">
          <code>{`// code_verifier: 43〜128 文字の URL-safe 乱数
const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
const challenge = base64url(await sha256(verifier));`}</code>
        </pre>

        <p>
          <strong>トークン交換:</strong> ユーザーが許可すると{' '}
          <code className="code-inline">redirect_uri?code=...&state=...</code> に戻ってきます。アプリの
          バックエンドから交換します。
        </p>
        <pre className="code-block">
          <code>{`POST ${base}/oauth/token
Content-Type: application/x-www-form-urlencoded

grant_type=authorization_code&code=...&client_id=...&client_secret=vhcs_...&code_verifier=...&redirect_uri=...`}</code>
        </pre>
        <p>レスポンス:</p>
        <pre className="code-block">
          <code>{`{
  "access_token": "vhat_...",
  "token_type": "Bearer",
  "expires_in": 3600,
  "refresh_token": "vhrt_...",
  "scope": "read:animations"
}`}</code>
        </pre>
        <p>
          以降は <code className="code-inline">Authorization: Bearer &lt;access_token&gt;</code> と{' '}
          <code className="code-inline">X-Api-Version: 1</code> を付けて API を呼び出します（
          <ChapterLink id="ch3">3章</ChapterLink>）。
        </p>

        <p>
          <strong>リフレッシュ:</strong> access_token の有効期限は1時間です。切れたら refresh_token で再発行
          します。access_token・refresh_token とも新しいものが発行され、<strong>古い refresh_token は無効に
          なります</strong>。
        </p>
        <pre className="code-block">
          <code>{`POST ${base}/oauth/token
Content-Type: application/x-www-form-urlencoded

grant_type=refresh_token&refresh_token=...&client_id=...&client_secret=vhcs_...`}</code>
        </pre>

        <p>
          <strong>失効（revoke）:</strong> access_token・refresh_token のどちらでも失効できます。トークンの
          有効・無効やペア失効の有無にかかわらず、常に <code className="code-inline">200</code> で空のレスポンス
          を返します。
        </p>
        <pre className="code-block">
          <code>{`POST ${base}/oauth/revoke
Content-Type: application/x-www-form-urlencoded

token=...&client_id=...&client_secret=vhcs_...`}</code>
        </pre>
        <p className="apidocs-muted">
          ユーザー自身がアプリとの連携を解除する場合は、マイページの「連携アプリ」タブから行えます。
        </p>

        <h3 className="apidocs-h3">2-4. アプリ専用トークン（client_credentials）</h3>
        <p>
          ユーザーのログイン・同意を経由せずに、アプリ自身の名義でアクセストークンを取得します。投稿者が
          連携アプリに「アプリ配信設定」したアニメーションを、アプリ単体で取得する用途に使います。
        </p>
        <pre className="code-block">
          <code>{`curl -X POST ${base}/oauth/token \\
  -d grant_type=client_credentials \\
  -d client_id=... \\
  -d client_secret=vhcs_...`}</code>
        </pre>
        <p>レスポンス:</p>
        <pre className="code-block">
          <code>{`{
  "access_token": "vhat_...",
  "token_type": "Bearer",
  "expires_in": 3600,
  "scope": "read:animations"
}`}</code>
        </pre>
        <p>
          有効期限は1時間で、<strong>refresh_token は発行されません</strong>。期限切れ後は再度このリクエストで
          取得してください。要求できるスコープは常に <code className="code-inline">read:animations</code>{' '}
          のみで、他のスコープを要求すると <code className="code-inline">400 invalid_scope</code> になります。
        </p>
        <p>
          できるのは、「アプリ配信設定」されたアニメーション + 公開（public）アニメーションの一覧取得・詳細
          取得・検索、ダウンロードライセンスの発行/取得/失効、および配信アセット限定の{' '}
          <code className="code-inline">GET /animations/{'{id}'}/file</code>（
          <ChapterLink id="ch5">5章</ChapterLink>）です。<code className="code-inline">/api/account</code>・
          <code className="code-inline">/api/account/animations</code>・
          <code className="code-inline">/api/hearts</code> はユーザー本人の情報を扱うため{' '}
          <code className="code-inline">403 app_token_not_allowed</code> になります。write 系スコープは発行
          されないため、投稿・更新・お気に入り追加等も不可です。
        </p>
        <div className="apidocs-callout">
          <span className="apidocs-callout__label">漏洩対策 — secret ローテーション</span>
          <p style={{ margin: 0 }}>
            <code className="code-inline">client_secret</code> を再発行または削除すると、発行済みの
            <strong>アプリ専用トークンはすべて即座に失効</strong>します（以降は{' '}
            <code className="code-inline">401</code>）。2-3節のユーザー認可トークンはこの影響を受けません。
          </p>
        </div>
      </section>

      {/* ============================================================ */}
      <section className="apidocs-chapter" id="ch3">
        <div className="apidocs-chapter__head">
          <span className="apidocs-chapter__num">3</span>
          <h2>共通仕様</h2>
        </div>

        <h3 className="apidocs-h3">3-1. 認証・共通ヘッダ</h3>
        <p>
          すべてのリクエストに、OAuth アクセストークンを使った{' '}
          <code className="code-inline">Authorization: Bearer vhat_...</code> と{' '}
          <code className="code-inline">X-Api-Version: 1</code> ヘッダが必須です。匿名アクセスや、Cognito ID
          トークンを使ったサイト自身のログインでは利用できず、<code className="code-inline">401</code>{' '}
          になります。以降の各エンドポイント節では、このヘッダ表記を繰り返しません。
        </p>
        <pre className="code-block">
          <code>{`curl -H "Authorization: Bearer vhat_..." -H "X-Api-Version: 1" \\
  ${base}/api/account/animations`}</code>
        </pre>

        <h3 className="apidocs-h3">3-2. レスポンスエンベロープ</h3>
        <p>
          <code className="code-inline">200</code> は{' '}
          <code className="code-inline">{'{ data, error: null, _links, rand }'}</code>、エラー時は{' '}
          <code className="code-inline">{'{ data: null, error: { code, message, details }, rand }'}</code>{' '}
          の形式で返します。<code className="code-inline">rand</code> は16文字の hex 乱数です。
          <code className="code-inline">201</code> は <code className="code-inline">{'_links: {}'}</code>、
          <code className="code-inline">204</code> は本文なしです。
        </p>
        <pre className="code-block">
          <code>{`{
  "data": ...,
  "error": null,
  "_links": { "next": { "href": "/api/account/animations?count=20&max_id=..." } },
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>

        <h3 className="apidocs-h3">3-3. ページング</h3>
        <p>
          ページングは <code className="code-inline">count</code> と{' '}
          <code className="code-inline">max_id</code> のクエリパラメータで指定します。
          <code className="code-inline">count</code> は 1〜100 で、既定値は20です。範囲外の値は丸められ、
          数値でない値は既定値になります。<code className="code-inline">max_id</code> には前ページ最後の id を
          指定します。次ページが存在するかどうかは <code className="code-inline">_links.next.href</code>{' '}
          の有無で判定してください。次ページが無い場合、<code className="code-inline">_links</code> は空
          オブジェクト <code className="code-inline">{'{}'}</code> になります。
          <code className="code-inline">max_id</code> が解決できない場合は <code className="code-inline">400</code>{' '}
          になります。
        </p>

        <h3 className="apidocs-h3">3-4. 利用上限（レート制限）</h3>
        <p>
          月間 <strong>50,000 リクエストまで無料</strong>で利用できます。<code className="code-inline">/api/*</code>{' '}
          は public な GET を含む全リクエストで1消費されます。<code className="code-inline">GET /animations/{'{id}'}/file</code>{' '}
          も、OAuth トークン経由の呼び出しは1消費されます。これを超える継続利用が必要な場合はお問い合わせより
          個別にご相談ください。上限を超えると <code className="code-inline">429 Too Many Requests</code>{' '}
          が返ります。カウントは UTC の月単位でリセットされ、利用状況は開発者ポータルのマイページで確認できます。
        </p>

        <h3 className="apidocs-h3">3-5. 型情報</h3>
        <p>
          以降の各エンドポイントが返す <code className="code-inline">Animation</code> オブジェクトの形状です。
        </p>
        <pre className="code-block">
          <code>{`interface Animation {
  id: string;
  title: string;
  description: string;
  author: string;          // 投稿者の表示名
  tags: string[];
  category?: string;       // 未設定は「未分類」扱い
  vrmaUrl?: string;        // private の応答では省略される（後述）
  thumbnailUrl?: string;
  likes: number;
  playCount: number;
  downloadCount: number;
  durationSec?: number;
  createdAt: string;       // ISO 8601
  visibility: 'public' | 'private';
  ownerId?: string;        // 投稿者のユーザーID
  credit?: {               // クレジット情報（任意）
    author?: string; license?: string; licenseUrl?: string;
    notice?: string; storeUrls?: string[];
  };
  usageTerms?: {           // 投稿者以外の利用条件（任意）
    violent: boolean; sexual: boolean; corporate: boolean;
    commercial: boolean; commercialScope?: 'profit' | 'nonprofit';
    redistribute: boolean; modify: boolean; creditRequired: boolean;
  };
  rating?: 'all' | 'sensitive';
  appClientIds?: string[]; // アプリ配信設定の配信先クライアントID
  encrypted: boolean;      // S3 上の実体が暗号化済みか
}`}</code>
        </pre>
        <p>
          <code className="code-inline">visibility: private</code> の応答には{' '}
          <code className="code-inline">vrmaUrl</code> は含まれません（<code className="code-inline">thumbnailUrl</code>{' '}
          は返ります）。全アセットの実体は公開設定にかかわらず AES-256-GCM で暗号化されており、
          <code className="code-inline">vrmaUrl</code> を直接 fetch しても暗号文しか得られません（復号鍵は
          サーバー側のみ保持）。平文 <code className="code-inline">.vrma</code> の正規の取得経路は{' '}
          <ChapterLink id="ch5">5章</ChapterLink>の2つのみです。
        </p>
      </section>

      {/* ============================================================ */}
      <section className="apidocs-chapter" id="ch4">
        <div className="apidocs-chapter__head">
          <span className="apidocs-chapter__num">4</span>
          <h2>エンドポイントリファレンス</h2>
        </div>

        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>エンドポイント</th>
                <th>説明</th>
                <th>scope</th>
                <th>
                  アプリ専用
                  <br />
                  トークン
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <MethodChip method="GET" /> <code className="code-inline">/api/account</code>
                </td>
                <td>トークンの持ち主自身の情報を取得</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={false} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="GET" /> <code className="code-inline">/api/account/animations</code>
                </td>
                <td>自分の投稿一覧を取得（visibility 不問）</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={false} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="GET" /> <code className="code-inline">/api/hearts</code>
                </td>
                <td>自分のお気に入り一覧を取得</td>
                <td>
                  <code className="code-inline">read:favorites</code>
                </td>
                <td>
                  <Yn yes={false} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="GET" /> <code className="code-inline">/api/animations</code>
                </td>
                <td>公開アニメーションの新着一覧を取得</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={true} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="GET" /> <code className="code-inline">/api/app/animations</code>
                </td>
                <td>自分のアプリに「アプリ配信設定」されたアニメーション一覧を取得（visibility 不問）</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={true} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="GET" /> <code className="code-inline">/api/animations/{'{id}'}</code>
                </td>
                <td>アニメーション詳細を取得（private は所有者本人・配信先アプリのみ）</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={true} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="GET" /> <code className="code-inline">/api/search/animations</code>
                </td>
                <td>キーワード検索（public のみ、部分一致）</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={true} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="POST" /> <code className="code-inline">/api/download_licenses</code>
                </td>
                <td>ダウンロードライセンスを発行</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={true} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="GET" /> <code className="code-inline">/api/download_licenses/{'{id}'}</code>
                </td>
                <td>ライセンス情報を取得</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={true} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="DELETE" /> <code className="code-inline">/api/download_licenses/{'{id}'}</code>
                </td>
                <td>ライセンスを失効</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={true} />
                </td>
              </tr>
              <tr>
                <td>
                  <MethodChip method="GET" />{' '}
                  <code className="code-inline">/api/download_licenses/{'{id}'}/download</code>
                </td>
                <td>実ファイルの一時 URL（5分間有効）へ 302 リダイレクト</td>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  <Yn yes={true} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="apidocs-muted">
          「アプリ専用トークン」列: client_credentials で取得したトークンで呼び出せるかどうか。✕ の
          エンドポイントは <code className="code-inline">403 app_token_not_allowed</code> になります。
        </p>
        <p className="apidocs-muted">
          <code className="code-inline">GET /animations/{'{id}'}/file</code> は{' '}
          <ChapterLink id="ch5">5章</ChapterLink>を参照してください。
        </p>

        {/* ---- GET /api/account ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="GET" /> <code className="code-inline">/api/account</code>
        </h3>
        <p>トークンの持ち主（ユーザー本人）の情報を取得します。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={false} />
          </span>
        </p>
        <pre className="code-block">
          <code>{`{
  "data": {
    "id": "<ユーザーID>",
    "name": "ニックネーム",
    "iconUrl": "https://..."
  },
  "error": null,
  "_links": {},
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>
        <p className="apidocs-muted">
          <code className="code-inline">iconUrl</code> はアイコン未設定なら省略されます。プロフィール未設定の
          場合、<code className="code-inline">name</code> は id と同じ値になります。
        </p>

        {/* ---- GET /api/account/animations ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="GET" /> <code className="code-inline">/api/account/animations</code>
        </h3>
        <p>自分の投稿一覧を取得します（visibility 不問、新着順）。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={false} />
          </span>
        </p>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>パラメータ</th>
                <th>必須</th>
                <th>型</th>
                <th>デフォルト</th>
                <th>説明</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code className="code-inline">count</code>
                </td>
                <td>任意</td>
                <td>整数</td>
                <td>20</td>
                <td>1〜100（範囲外は丸め、非数値は既定値）</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">max_id</code>
                </td>
                <td>任意</td>
                <td>文字列</td>
                <td>—</td>
                <td>前ページ最後の id</td>
              </tr>
            </tbody>
          </table>
        </div>
        <pre className="code-block">
          <code>{`{
  "data": [
    {
      "id": "dance-03",
      "title": "ダンスモーション A（下書き）",
      "visibility": "private",
      "thumbnailUrl": "https://.../thumbs/dance-03.jpg",
      "likes": 0,
      "encrypted": true
      // vrmaUrl は private のため含まれない。ほかのフィールドは3-5節を参照
    }
  ],
  "error": null,
  "_links": {},
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>
        <p>
          <strong>エラー:</strong>
        </p>
        <ul>
          <li>
            <code className="code-inline">max_id</code> が不正: <code className="code-inline">400 bad_request</code>
          </li>
        </ul>

        {/* ---- GET /api/hearts ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="GET" /> <code className="code-inline">/api/hearts</code>
        </h3>
        <p>自分のお気に入り一覧を取得します（新着順）。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:favorites</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={false} />
          </span>
        </p>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>パラメータ</th>
                <th>必須</th>
                <th>型</th>
                <th>デフォルト</th>
                <th>説明</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code className="code-inline">count</code>
                </td>
                <td>任意</td>
                <td>整数</td>
                <td>20</td>
                <td>1〜100（範囲外は丸め、非数値は既定値）</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">max_id</code>
                </td>
                <td>任意</td>
                <td>文字列</td>
                <td>—</td>
                <td>前ページ最後の id</td>
              </tr>
            </tbody>
          </table>
        </div>
        <pre className="code-block">
          <code>{`{
  "data": [
    {
      "id": "dance-03",
      "title": "ダンスモーション A",
      "visibility": "public",
      "vrmaUrl": "https://.../vrma/dance-03.vrma",
      "thumbnailUrl": "https://.../thumbs/dance-03.jpg",
      "likes": 12,
      "encrypted": true
      // ほかのフィールドは3-5節の Animation を参照
    }
  ],
  "error": null,
  "_links": {},
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>
        <p className="apidocs-muted">
          対象は public または自分の投稿のみです。自分の private なお気に入りは <code className="code-inline">vrmaUrl</code>{' '}
          が省略されます。
        </p>

        {/* ---- GET /api/animations ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="GET" /> <code className="code-inline">/api/animations</code>
        </h3>
        <p>公開アニメーションの新着一覧を取得します。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={true} />
          </span>
        </p>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>パラメータ</th>
                <th>必須</th>
                <th>型</th>
                <th>デフォルト</th>
                <th>説明</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code className="code-inline">count</code>
                </td>
                <td>任意</td>
                <td>整数</td>
                <td>20</td>
                <td>1〜100（範囲外は丸め、非数値は既定値）</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">max_id</code>
                </td>
                <td>任意</td>
                <td>文字列</td>
                <td>—</td>
                <td>前ページ最後の id</td>
              </tr>
            </tbody>
          </table>
        </div>
        <pre className="code-block">
          <code>{`{
  "data": [
    {
      "id": "dance-03",
      "title": "ダンスモーション A",
      "visibility": "public",
      "vrmaUrl": "https://.../vrma/dance-03.vrma",
      "thumbnailUrl": "https://.../thumbs/dance-03.jpg",
      "likes": 12,
      "encrypted": true
      // ほかのフィールドは3-5節の Animation を参照
    }
  ],
  "error": null,
  "_links": { "next": { "href": "/api/animations?count=20&max_id=dance-03" } },
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>
        <div className="apidocs-callout">
          <span className="apidocs-callout__label">サイト自身が使う GET /animations との違い</span>
          <p style={{ margin: 0 }}>
            この <code className="code-inline">/api/animations</code>（<code className="code-inline">/api/*</code>{' '}
            名前空間、本ドキュメントの対象）とは別に、VRMA Hub サイト自身が使う{' '}
            <code className="code-inline">GET /animations</code>（<code className="code-inline">/api/*</code>{' '}
            外）が存在します。こちらは認証の有無・種類に関わらず常に公開アニメーションのみを返す
            サイト内部向けのエンドポイントで、以前は <code className="code-inline">Bearer vhat_...</code>{' '}
            を付けて呼ぶとアプリ配信設定分・トークン本人の投稿分も混在して返っていましたが、その挙動は
            廃止されました。連携アプリからアプリ配信設定されたアニメーションの一覧を取得したい場合は、
            <code className="code-inline">GET /animations</code> ではなく次に示す{' '}
            <code className="code-inline">GET /api/app/animations</code> を使ってください。
          </p>
        </div>

        {/* ---- GET /api/app/animations ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="GET" /> <code className="code-inline">/api/app/animations</code>
        </h3>
        <p>
          トークンのアプリ（<code className="code-inline">client_id</code>）に投稿者が「アプリ配信設定」
          （<ChapterLink id="ch1">1章</ChapterLink>）したアニメーションの一覧を取得します（新着順）。
          <code className="code-inline">visibility</code> を問わず、配信設定されたものだけが対象です
          （配信設定されていない public アニメーションは含まれません。それを取得したい場合は前述の{' '}
          <code className="code-inline">GET /api/animations</code> を使ってください）。
        </p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={true} />
          </span>
        </p>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>パラメータ</th>
                <th>必須</th>
                <th>型</th>
                <th>デフォルト</th>
                <th>説明</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code className="code-inline">count</code>
                </td>
                <td>任意</td>
                <td>整数</td>
                <td>20</td>
                <td>1〜100（範囲外は丸め、非数値は既定値）</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">max_id</code>
                </td>
                <td>任意</td>
                <td>文字列</td>
                <td>—</td>
                <td>前ページ最後の id</td>
              </tr>
            </tbody>
          </table>
        </div>
        <pre className="code-block">
          <code>{`{
  "data": [
    {
      "id": "dance-03",
      "title": "ダンスモーション A（下書き）",
      "visibility": "private",
      "thumbnailUrl": "https://.../thumbs/dance-03.jpg",
      "likes": 0,
      "encrypted": true
      // vrmaUrl は private のため含まれない。ほかのフィールドは3-5節を参照
    }
  ],
  "error": null,
  "_links": {},
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>
        <p className="apidocs-muted">
          private の応答には <code className="code-inline">vrmaUrl</code> が含まれません（ユーザー認可トークン・
          アプリ専用トークンとも共通。取得は <code className="code-inline">GET /animations/{'{id}'}/file</code>{' '}
          または <ChapterLink id="ch5">ダウンロードライセンス</ChapterLink> 経由で行ってください）。
        </p>

        {/* ---- GET /api/animations/{id} ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="GET" /> <code className="code-inline">/api/animations/{'{id}'}</code>
        </h3>
        <p>アニメーションの詳細を取得します。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={true} />
          </span>
        </p>
        <pre className="code-block">
          <code>{`{
  "data": {
    "id": "dance-03",
    "title": "ダンスモーション A",
    "visibility": "public",
    "vrmaUrl": "https://.../vrma/dance-03.vrma",
    "thumbnailUrl": "https://.../thumbs/dance-03.jpg",
    "likes": 12,
    "encrypted": true
    // ほかのフィールドは3-5節の Animation を参照
  },
  "error": null,
  "_links": {},
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>
        <p className="apidocs-muted">
          private は所有者本人、または配信先アプリのトークンのみ閲覧できます。private の応答には{' '}
          <code className="code-inline">vrmaUrl</code> は含まれません。
        </p>
        <p>
          <strong>エラー:</strong>
        </p>
        <ul>
          <li>
            存在しない、またはアクセス権のない private:{' '}
            <code className="code-inline">404 not_found</code>（存在を隠すため）
          </li>
        </ul>

        {/* ---- GET /api/search/animations ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="GET" /> <code className="code-inline">/api/search/animations</code>
        </h3>
        <p>キーワードでアニメーションを検索します。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={true} />
          </span>
        </p>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>パラメータ</th>
                <th>必須</th>
                <th>型</th>
                <th>デフォルト</th>
                <th>説明</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code className="code-inline">keyword</code>
                </td>
                <td>必須</td>
                <td>文字列</td>
                <td>—</td>
                <td>
                  title / description / author / tags の部分一致（大小文字は区別しない）
                </td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">count</code>
                </td>
                <td>任意</td>
                <td>整数</td>
                <td>20</td>
                <td>1〜100（範囲外は丸め、非数値は既定値）</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">max_id</code>
                </td>
                <td>任意</td>
                <td>文字列</td>
                <td>—</td>
                <td>前ページ最後の id</td>
              </tr>
            </tbody>
          </table>
        </div>
        <pre className="code-block">
          <code>{`{
  "data": [
    {
      "id": "dance-03",
      "title": "ダンスモーション A",
      "visibility": "public",
      "vrmaUrl": "https://.../vrma/dance-03.vrma",
      "thumbnailUrl": "https://.../thumbs/dance-03.jpg",
      "likes": 12,
      "encrypted": true
      // ほかのフィールドは3-5節の Animation を参照
    }
  ],
  "error": null,
  "_links": {},
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>
        <p className="apidocs-muted">
          public のみが対象です。新着から最大約2,000件の範囲でのみ評価するため、それより古い投稿はヒットしない
          場合があります。
        </p>
        <p>
          <strong>エラー:</strong>
        </p>
        <ul>
          <li>
            <code className="code-inline">keyword</code> が欠落:{' '}
            <code className="code-inline">400 bad_request</code>
          </li>
        </ul>

        {/* ---- POST /api/download_licenses ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="POST" /> <code className="code-inline">/api/download_licenses</code>
        </h3>
        <p>ダウンロードライセンスを発行します。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={true} />
          </span>
        </p>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>パラメータ（body）</th>
                <th>必須</th>
                <th>型</th>
                <th>デフォルト</th>
                <th>説明</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code className="code-inline">animation_id</code>
                </td>
                <td>必須</td>
                <td>文字列</td>
                <td>—</td>
                <td>対象アニメーションの id</td>
              </tr>
            </tbody>
          </table>
        </div>
        <pre className="code-block">
          <code>{`{
  "data": {
    "id": "<ライセンスID(UUID)>",
    "animation_id": "...",
    "created_at": "<ISO8601>",
    "expires_at": 1234567890
  },
  "error": null,
  "_links": {},
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>
        <p className="apidocs-muted">
          有効期間は発行から1時間です。発行条件（<ChapterLink id="ch5">5章</ChapterLink>）を満たさない場合や
          対象が存在しない場合は <code className="code-inline">404</code> です。
        </p>
        <p>
          <strong>エラー:</strong>
        </p>
        <ul>
          <li>
            発行条件を満たさない、または対象が存在しない:{' '}
            <code className="code-inline">404 not_found</code>
          </li>
        </ul>

        {/* ---- GET /api/download_licenses/{id} ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="GET" /> <code className="code-inline">/api/download_licenses/{'{id}'}</code>
        </h3>
        <p>発行済みライセンスの情報を取得します。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={true} />
          </span>
        </p>
        <pre className="code-block">
          <code>{`{
  "data": {
    "id": "<ライセンスID(UUID)>",
    "animation_id": "...",
    "created_at": "<ISO8601>",
    "expires_at": 1234567890
  },
  "error": null,
  "_links": {},
  "rand": "1a2b3c4d5e6f7a8b"
}`}</code>
        </pre>
        <p>
          <strong>エラー:</strong>
        </p>
        <ul>
          <li>
            発行時と異なるユーザー・異なるアプリのトークン、期限切れ、存在しない:{' '}
            <code className="code-inline">404 not_found</code>
          </li>
        </ul>

        {/* ---- DELETE /api/download_licenses/{id} ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="DELETE" /> <code className="code-inline">/api/download_licenses/{'{id}'}</code>
        </h3>
        <p>ライセンスを失効させます。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={true} />
          </span>
        </p>
        <p className="apidocs-muted">
          成功時は <code className="code-inline">204</code>（本文なし）を返します。
        </p>

        {/* ---- GET /api/download_licenses/{id}/download ---- */}
        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <MethodChip method="GET" /> <code className="code-inline">/api/download_licenses/{'{id}'}/download</code>
        </h3>
        <p>実ファイルの一時 URL へ 302 リダイレクトします。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
          <span>
            アプリ専用トークン: <Yn yes={true} />
          </span>
        </p>
        <pre className="code-block">
          <code>{`HTTP/1.1 302 Found
Location: https://<bucket>.s3.amazonaws.com/...（5分間有効な署名付き URL）`}</code>
        </pre>
        <p className="apidocs-muted">
          呼び出し時に発行条件を再評価します。発行後に private 化・削除された場合は{' '}
          <code className="code-inline">404</code> です。一時 URL の有効期限内であれば複数回呼び出せます
          （1ライセンス = 1回ではありません）。
        </p>
      </section>

      {/* ============================================================ */}
      <section className="apidocs-chapter" id="ch5">
        <div className="apidocs-chapter__head">
          <span className="apidocs-chapter__num">5</span>
          <h2>ファイルのダウンロード</h2>
        </div>

        <p>
          実ファイル（平文の <code className="code-inline">.vrma</code>）を取得できる正規の経路は、次の
          <strong>2つのみ</strong>です。全アセットの実体は S3 上で AES-256-GCM 暗号化されているため、どちらの
          経路もサーバー側で復号してから応答します。
        </p>
        <ul>
          <li>
            <strong>ダウンロードライセンス</strong>（5-1・5-2） —
            「ライセンス発行 → ダウンロード」の2段階方式（VRoid Hub 互換）
          </li>
          <li>
            <strong>
              <code className="code-inline">GET /animations/{'{id}'}/file</code>
            </strong>
            （5-3） — 所有者本人・配信先アプリ限定の直接取得
          </li>
        </ul>

        <h3 className="apidocs-h3">5-1. ダウンロードライセンスの手順</h3>
        <pre className="code-block">
          <code>{`# 1. ライセンスを発行する（201）
curl -X POST -H "Authorization: Bearer vhat_..." -H "X-Api-Version: 1" \\
  -H "Content-Type: application/json" \\
  -d '{"animation_id":"dance-03"}' ${base}/api/download_licenses
# => { "data": { "id": "...", "animation_id": "dance-03", "created_at": "...", "expires_at": 1234567890 }, ... }

# 2. 発行から1時間以内にダウンロードする（302 → 一時 URL）
curl -L -H "Authorization: Bearer vhat_..." -H "X-Api-Version: 1" \\
  ${base}/api/download_licenses/{id}/download -o dance-03.vrma`}</code>
        </pre>

        <h3 className="apidocs-h3">5-2. 発行条件</h3>
        <p>次のいずれかを満たす場合に発行できます。</p>
        <ol>
          <li>アセットの所有者本人のトークン</li>
          <li>アプリ配信設定で配信先に指定されているアプリのトークン</li>
          <li>
            <code className="code-inline">visibility: public</code>{' '}
            （公開アセットは誰でもダウンロードできます）
          </li>
        </ol>
        <div className="apidocs-callout apidocs-callout--warn">
          <span className="apidocs-callout__label">注意</span>
          <ul style={{ margin: 0 }}>
            <li>
              <code className="code-inline">/download</code> 呼び出し時には発行条件を再評価します。発行後に
              private 化・削除された場合は <code className="code-inline">404</code> になります。
            </li>
            <li>一時 URL は発行から5分で失効します。取得した URL を第三者と共有しないでください。</li>
            <li>ブラウザから一時 URL を直接 fetch する場合、CORS 設定によりブロックされることがあります。</li>
            <li>暗号化アセットは、サーバー側で復号した平文ファイルへの一時 URL に 302 します。</li>
          </ul>
        </div>

        <h3 className="apidocs-h3 apidocs-endpoint-h3">
          <span>5-3.</span>
          <MethodChip method="GET" /> <code className="code-inline">/animations/{'{id}'}/file</code>
        </h3>
        <p>ライセンスの発行を経由せず、1回のリクエストで実ファイル本体を取得します。</p>
        <p className="apidocs-meta">
          <span>
            scope: <code className="code-inline">read:animations</code>
          </span>
        </p>
        <p>
          <strong>所有者本人、または配信先アプリ（<code className="code-inline">appClientIds</code> に
          指定されたアプリ）のトークン専用</strong>です。public でもそれ以外のトークンからは{' '}
          <code className="code-inline">403</code>（ダウンロードライセンスの利用を促すエラー）、private は
          存在を隠すため <code className="code-inline">404</code> になります。
        </p>
        <pre className="code-block">
          <code>{`curl -H "Authorization: Bearer vhat_..." -H "X-Api-Version: 1" \\
  ${base}/animations/dance-03/file -o dance-03.vrma`}</code>
        </pre>
        <p>
          <strong>エラー・注意:</strong>
        </p>
        <ul>
          <li>
            <code className="code-inline">/api/*</code> のエンベロープではなく、
            <code className="code-inline">application/octet-stream</code>{' '}
            の実ファイル本体（サーバー側で復号済みの平文）を <code className="code-inline">200</code>{' '}
            で直接返します。パスも <code className="code-inline">/api/*</code> の外にあります。
          </li>
          <li>
            約4.5MB 超のファイルは <code className="code-inline">413</code>{' '}
            になります。大きいファイルはダウンロードライセンス（5-1・5-2）を利用してください。
          </li>
        </ul>
      </section>

      {/* ============================================================ */}
      <section className="apidocs-chapter" id="ch6">
        <div className="apidocs-chapter__head">
          <span className="apidocs-chapter__num">6</span>
          <h2>付録 — スコープ・エラーコード</h2>
        </div>

        <h3 className="apidocs-h3">6-1. スコープ一覧</h3>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>スコープ</th>
                <th>内容</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code className="code-inline">read:animations</code>
                </td>
                <td>
                  private を含む自分の投稿や、投稿者がこのアプリに「アプリ配信設定」したアニメーション、
                  公開アニメーションの新着一覧を読む（
                  <code className="code-inline">GET /api/account</code>、
                  <code className="code-inline">GET /api/account/animations</code>、
                  <code className="code-inline">GET /api/animations</code>、
                  <code className="code-inline">GET /api/app/animations</code>、
                  <code className="code-inline">GET /api/animations/{'{id}'}</code>、
                  <code className="code-inline">GET /api/search/animations</code>、
                  <code className="code-inline">/api/download_licenses</code> 系）
                </td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">read:favorites</code>
                </td>
                <td>
                  <code className="code-inline">GET /api/hearts</code> を読む
                </td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">write:animations</code>
                </td>
                <td>現在 <code className="code-inline">/api/*</code> 名前空間には対応する書き込みエンドポイントはありません</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">write:favorites</code>
                </td>
                <td>現在 <code className="code-inline">/api/*</code> 名前空間には対応する書き込みエンドポイントはありません</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="apidocs-h3">6-2. OAuth のエラーコード</h3>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>エラー</th>
                <th>内容</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code className="code-inline">invalid_client</code>
                </td>
                <td>client_id が不正・未承認・存在しない。または client_secret が欠落・不一致</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">invalid_redirect_uri</code>
                </td>
                <td>redirect_uri が登録済みの値と一致しない</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">invalid_scope</code>
                </td>
                <td>要求した scope が許可された範囲を超えている</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">invalid_grant</code>
                </td>
                <td>認可コード・refresh_token が無効・期限切れ・使用済み、または PKCE 検証に失敗</td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">unauthorized_client</code>
                </td>
                <td>
                  <code className="code-inline">grant_type=client_credentials</code>{' '}
                  を client_secret 未発行の開発者で呼び出した
                </td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">unsupported_grant_type</code>
                </td>
                <td>
                  <code className="code-inline">grant_type</code> が{' '}
                  <code className="code-inline">authorization_code</code> /{' '}
                  <code className="code-inline">refresh_token</code> /{' '}
                  <code className="code-inline">client_credentials</code> のいずれでもない
                </td>
              </tr>
              <tr>
                <td>
                  <code className="code-inline">access_denied</code>
                </td>
                <td>ユーザーが同意画面で拒否した</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="apidocs-h3">6-3. /api/* のエラーコード</h3>
        <div className="apidocs-table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>HTTP</th>
                <th>コード</th>
                <th>内容</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>400</td>
                <td>
                  <code className="code-inline">bad_request</code>
                </td>
                <td>
                  パラメータ不正（<code className="code-inline">keyword</code> 欠落、
                  <code className="code-inline">max_id</code> 不正、
                  <code className="code-inline">animation_id</code> 欠落など）
                </td>
              </tr>
              <tr>
                <td>400</td>
                <td>
                  <code className="code-inline">invalid_api_version</code>
                </td>
                <td>
                  <code className="code-inline">X-Api-Version</code> ヘッダの欠落または不正な値
                </td>
              </tr>
              <tr>
                <td>401</td>
                <td>
                  <code className="code-inline">invalid_token</code>
                </td>
                <td>アクセストークンが無効・期限切れ・失効済み</td>
              </tr>
              <tr>
                <td>403</td>
                <td>
                  <code className="code-inline">app_token_not_allowed</code>
                </td>
                <td>アプリ専用トークンでユーザー文脈のエンドポイントを呼び出した</td>
              </tr>
              <tr>
                <td>403</td>
                <td>
                  <code className="code-inline">insufficient_scope</code>
                </td>
                <td>トークンに必要なスコープがない</td>
              </tr>
              <tr>
                <td>404</td>
                <td>
                  <code className="code-inline">not_found</code>
                </td>
                <td>対象が存在しない、またはアクセス権のない private アセット（存在を隠すため 404）</td>
              </tr>
              <tr>
                <td>429</td>
                <td>
                  <code className="code-inline">rate_limit_exceeded</code>
                </td>
                <td>月間の無料利用上限（50,000リクエスト）を超えた</td>
              </tr>
              <tr>
                <td>500</td>
                <td>
                  <code className="code-inline">internal_error</code>
                </td>
                <td>サーバー内部エラー</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="apidocs-h3">6-4. 利用上の注意</h3>
        <p>
          API のご利用にあたっては、<Link to="/terms">利用規約</Link>および
          <Link to="/guidelines">コミュニティガイドライン</Link>
          を遵守してください。取得したデータの再配布・二次利用の範囲もこれらに準じます。
        </p>
      </section>
    </div>
  );
}
